import {Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, model} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DynamicComponentBase, NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import {MwcCheckboxControlValueAccessorDirective} from '../../directives/mwc-checkbox-control-value-accessor.directive';
import {Alerts} from '../../lib/alerts';
import {TechExAGORAAPIsApi} from '../../../api-client-py/src';
import {AngularUtils} from '../../lib/angular';

@Component({
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        FormsModule,
        MwcCheckboxControlValueAccessorDirective
    ],
    template: `
        <div class="fullsize-vbox" style="gap: 5px">
            <div class="fullsize-hbox" style="flex: 1; gap: 5px; align-items: flex-start">
                <textarea id="agoraFetchText" [(ngModel)]="idsText"></textarea>
                <div class="layout-vbox" style="width: fit-content; gap: 5px;">
                    <h4>Force Overwrite</h4>
                    <fluent-field label-position="before">
                        <label slot="label">Expiring</label>
                        <fluent-checkbox slot="input" ngChkboxDefaultControl [(ngModel)]="forceExpiring"></fluent-checkbox>
                    </fluent-field>
                    <fluent-field label-position="before">
                        <label slot="label">Renewal</label>
                        <fluent-checkbox slot="input" ngChkboxDefaultControl [(ngModel)]="forceRenewal"></fluent-checkbox>
                    </fluent-field>
                </div>
            </div>
            <div style="font-style: italic; font-size: 11px">
                <i class="fal fa-info-circle"></i>
                Enter account (IT-CAS-***) or submission IDs (SUB-78***) to fetch from AGORA.
                IDs can be from any, expiring or renewed accounts, to indicate the row to be updated.
                Multiple IDs can be comma or newline separated.
                Data updates are applied on the server immediately (~5min).
                Account information is overwritten only when AGORA data is newer.
                To force overwriting, use the checkboxes.
                Only "Open" accounts are updated.
            </div>
            <div class="toolbar-horizontal" style="justify-content: flex-end; border-top: 1px solid lightgray">
                <fluent-button (click)="onCancel()">Cancel</fluent-button>
                <fluent-button appearance="primary" (click)="onStartFetch()" [disabled]="startBtnDisabled()">Start Fetch</fluent-button>
            </div>
        </div>
    `,
    styles: `
        textarea {
            flex: 1;
            resize: none;
            height: 100%;
        }
        h4 {
            margin: 0;
        }
    `
})
export class AgoraFetchDialogComponent extends DynamicComponentBase {

    public readonly portfolioId = input.required<string>();

    protected readonly forceExpiring = model(false);
    protected readonly forceRenewal = model(false);

    protected readonly idsText = model('');
    protected readonly startBtnDisabled = computed(() => {
        return AngularUtils.trueOrUndefined(this.idsText().trim().length === 0);
    });

    private readonly techExAgoraApi = inject(TechExAGORAAPIsApi);

    protected onCancel() {
        this.dynPopup()?.hide();
    }

    protected async onStartFetch() {
        const ids = this.idsText().split(/[\n,;]+/).map(id => id.trim()).filter(id => !!id);
        if (ids.length === 0) return;

        setTimeout(() => this.dynPopup()?.hide());

        try {
            await this.techExAgoraApi.agoraFetchAgoraEtlTrackingAgorafetchPortfolioIdPost({
                portfolioId: this.portfolioId(), agoraIds: ids,
                forceExpiring: this.forceExpiring(), forceRenewal: this.forceRenewal()
            });
            await Alerts.alertPopup({ body: 'Request started' });
        } catch (e) {
            await Alerts.alertPopup({ body: `AGORA fetch failed: ${e}` });
        }
    }

    static async show(dynCompSvc: NgDynamicComponentsService, portfolioId: string, ids: string[]) {
        await dynCompSvc.createComponentDialog(AgoraFetchDialogComponent, {
            title: 'AGORA Live Data Fetch',
            height: '300px',
            showCloseButton: false,
            input: { portfolioId, idsText: ids.map(id => id.trim()).join('\n') }
        });
    }
}
